import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode, useEditor } from '@craftjs/core';

import { EDITOR_RESOLVERS } from '@common/constants';
import { createNewCellOnTableDrop } from './utils';
import { calcNodesWidth } from '@common/helpers/helpers';

const TableRow = ({ children, elementProps = { isFixed: false } }) => {
  const {
    connectors: { connect },
    actions: { setCustom },
    id,
    parentId,
    childNodes,
  } = useNode(({ data }) => ({
    parentId: data.parent,
    childNodes: data?.nodes,
  }));

  const { isFixed } = elementProps;

  const { query, actions, allNodes } = useEditor((state) => ({
    allNodes: state.nodes,
  }));

  const { t } = useTranslation();
  const ref = useRef(null);

  const tableNode = query.node(parentId).get();
  const showHeader = tableNode?.data?.props?.showHeader;
  const totalWidth = !showHeader ? calcNodesWidth(childNodes, allNodes) : 0;
  const hasOverflow = totalWidth > 100;

  useEffect(() => {
    if (ref?.current && connect) {
      connect(ref.current);
    }
  }, [connect]);

  useEffect(() => {
    if (!showHeader) {
      actions.history.ignore(() => {
        setTimeout(() => {
          actions.setProp(parentId, (props) => {
            props.isValid = !hasOverflow;
          });
        }, 0);
      });
    }
  }, [hasOverflow, showHeader]);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('table_row');
      });
    });
  }, [setCustom, t]);

  useEffect(() => {
    createNewCellOnTableDrop(id, query, actions, { isFixed: isFixed });
  }, []);

  return (
    <>
      {isFixed ? (
        <tbody>
          <tr
            ref={ref}
            style={{
              fontSize: 'inherit',
              outline: hasOverflow ? '1px solid red' : 'none',
            }}
          >
            {children}
          </tr>
        </tbody>
      ) : (
        <tbody data-hbs-each={tableNode?.data?.props?.listSrc}>
          <tr
            ref={ref}
            style={{
              fontSize: 'inherit',
              outline: hasOverflow ? '1px solid red' : 'none',
            }}
          >
            {children}
          </tr>
        </tbody>
      )}
      {hasOverflow && (
        <tr>
          <td colSpan={100} style={{ color: 'red', fontSize: '12px' }}>
            {`${t('total_width')}: ${totalWidth}%`}
          </td>
        </tr>
      )}
    </>
  );
};

const TableRowConfigPanel = () => {
  return <div></div>;
};

export const TableRowDefaultProps = {
  children: null,
};

TableRow.defaultProps = TableRowDefaultProps;

TableRow.propTypes = {
  children: PropTypes.node,
};

TableRow.craft = {
  displayName: EDITOR_RESOLVERS.COMMON.TABLE_ROW,
  name: EDITOR_RESOLVERS.COMMON.TABLE_ROW,
  props: TableRowDefaultProps,
  related: { settings: TableRowConfigPanel },
  rules: {
    isDeletable: () => false,
    canMoveIn: (incoming, self, helper) => {
      return !(
        self.data.props.elementProps.isFixed &&
        incoming[0]?.data?.name === 'Placeholder'
      );
    },
  },
};

export default TableRow;
